import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Calendar, User, Tag, ArrowLeft, Clock, Eye, Building2, TrendingUp, Award } from 'lucide-react';
import { useBooking } from '../context/BookingContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Case Study List Page
export const CaseStudyListPage = () => {
  const { openBooking, openContact } = useBooking();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/blog?published_only=true`);
        const data = await res.json();
        setPosts(data);
      } catch (e) {
        console.error('Failed to load case studies');
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const categories = ['all', ...new Set(posts.map(p => p.category).filter(Boolean))];
  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen bg-page-bg">
      <SEOHead 
        title="Case Studies | GetCare 360 - NDIS Provider Success Stories Australia"
        description="Discover how NDIS providers across Australia have transformed their operations with GetCare's custom-built AI platforms. Real results from real providers."
        keywords="NDIS case studies, disability care success stories, NDIS provider testimonials, software transformation Australia"
        canonicalUrl="/case-studies"
        page="case-studies"
      />
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-navy via-[#0d2847] to-primary-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-care-teal rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-tech-blue rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full border border-white/20 mb-6">
            <Award className="text-heritage-orange mr-2" size={16} />
            <span className="text-sm font-medium">Real Results from Real Providers</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Case <span className="text-care-teal">Studies</span>
          </h1>
          <p className="text-xl text-white/80 max-w-2xl">
            Discover how NDIS providers across Australia have transformed their operations with GetCare's custom-built platforms
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-surface border-b border-border-line sticky top-[73px] z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors capitalize ${
                  selectedCategory === cat
                    ? 'bg-tech-blue text-white'
                    : 'bg-page-bg text-text-secondary hover:bg-soft-sky'
                }`}
                data-testid={`filter-${cat}`}
              >
                {cat === 'all' ? 'All Case Studies' : cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center text-text-secondary py-12">
              <div className="animate-pulse">Loading case studies...</div>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <Building2 className="mx-auto text-text-secondary/30 mb-4" size={64} />
              <h3 className="text-xl font-bold text-text-primary mb-2">No Case Studies Yet</h3>
              <p className="text-text-secondary mb-6">
                We're working on documenting our success stories. Check back soon!
              </p>
              <div className="flex gap-3 justify-center">
                <Button onClick={openBooking} className="bg-tech-blue hover:bg-tech-blue/90 text-white">
                    Book a Demo
                  </Button>
                <Button onClick={openContact} variant="outline" className="border-tech-blue text-tech-blue hover:bg-tech-blue hover:text-white">
                    Contact Us
                  </Button>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Link key={post.id} to={`/case-studies/${post.slug}`} data-testid={`case-study-${post.slug}`}>
                  <Card className="border-border-line hover:border-tech-blue transition-all duration-300 hover:shadow-xl h-full group overflow-hidden">
                    {post.featured_image && (
                      <div className="h-48 overflow-hidden relative">
                        <img 
                          src={post.featured_image} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                        <div className="absolute bottom-4 left-4 right-4">
                          <span className="text-xs px-2 py-1 rounded bg-care-teal text-white">
                            {post.category || 'Case Study'}
                          </span>
                        </div>
                      </div>
                    )}
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className="text-xs text-text-secondary flex items-center">
                          <Clock size={12} className="mr-1" />
                          {Math.ceil(post.content?.length / 1000) || 3} min read
                        </span>
                        {post.views > 0 && (
                          <span className="text-xs text-text-secondary flex items-center">
                            <Eye size={12} className="mr-1" />
                            {post.views} views
                          </span>
                        )}
                      </div>
                      <h2 className="text-xl font-bold text-text-primary mb-2 group-hover:text-tech-blue transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                      <p className="text-text-secondary text-sm line-clamp-3 mb-4">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-text-secondary pt-4 border-t border-border-line">
                        <span className="flex items-center">
                          <Building2 size={12} className="mr-1" />
                          {post.author || 'GetCare Team'}
                        </span>
                        <span className="flex items-center text-care-teal font-medium">
                          Read Story
                          <TrendingUp size={12} className="ml-1" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-navy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Be Our Next <span className="text-care-teal">Success Story</span>?
          </h2>
          <p className="text-xl text-white/70 mb-8">
            Join 500+ NDIS providers who have transformed their operations with GetCare
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openBooking} className="bg-care-teal hover:bg-care-teal/90 text-white px-8 py-6 text-lg">
                Book a Demo
              </Button>
            <Button onClick={openContact} variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Contact Us
              </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Single Case Study Page with Preview Mode
export const CaseStudyPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        // Check if this is a preview request
        const urlParams = new URLSearchParams(window.location.search);
        const previewMode = urlParams.get('preview') === 'true';
        setIsPreview(previewMode);
        
        const res = await fetch(`${API_URL}/api/cms/blog/${slug}${previewMode ? '?preview=true' : ''}`);
        if (!res.ok) throw new Error('Case study not found');
        const data = await res.json();
        setPost(data);
      } catch (e) {
        console.error('Failed to load case study');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-page-bg">
        <Header />
        <div className="pt-32 pb-16 text-center text-text-secondary">
          <div className="animate-pulse">Loading case study...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-page-bg">
        <Header />
        <div className="pt-32 pb-16 text-center">
          <Building2 className="mx-auto text-text-secondary/30 mb-4" size={64} />
          <h1 className="text-2xl font-bold text-text-primary mb-4">Case Study Not Found</h1>
          <Link to="/case-studies">
            <Button className="bg-tech-blue hover:bg-tech-blue/90">
              <ArrowLeft size={18} className="mr-2" />
              Back to Case Studies
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page-bg">
      <SEOHead 
        title={`${post.title} | GetCare 360 Case Study`}
        description={post.excerpt || 'Read this NDIS provider success story from GetCare 360.'}
        keywords="NDIS case study, disability care transformation, provider success story"
        canonicalUrl={`/case-studies/${slug}`}
        page="case-studies"
        ogType="article"
        structuredData={{
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt,
          "datePublished": post.published_at,
          "author": {
            "@type": "Organization",
            "name": "GetCare 360"
          }
        }}
      />
      <Header />
      
      {/* Preview Banner */}
      {isPreview && (
        <div className="fixed top-[73px] left-0 right-0 bg-heritage-orange text-white py-2 px-4 text-center z-50" data-testid="preview-banner">
          <span className="font-medium">Preview Mode</span> - This case study is not published yet
        </div>
      )}
      
      {/* Hero */}
      <section className={`${isPreview ? 'pt-44' : 'pt-32'} pb-8 bg-gradient-to-br from-primary-navy via-[#0d2847] to-primary-navy text-white`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/case-studies" className="inline-flex items-center text-white/70 hover:text-white mb-6 transition-colors">
            <ArrowLeft size={18} className="mr-2" />
            Back to Case Studies
          </Link>
          
          <div className="flex items-center space-x-4 mb-4">
            <span className="px-3 py-1 rounded-full bg-care-teal/20 text-care-teal text-sm">
              {post.category || 'Case Study'}
            </span>
            {post.views > 0 && (
              <span className="flex items-center text-white/60 text-sm">
                <Eye size={14} className="mr-1" />
                {post.views} views
              </span>
            )}
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-4 text-white/70">
            <span className="flex items-center">
              <Building2 size={16} className="mr-2" />
              {post.author || 'GetCare Team'}
            </span>
            <span className="flex items-center">
              <Calendar size={16} className="mr-2" />
              {new Date(post.created_at).toLocaleDateString('en-AU', { 
                year: 'numeric', month: 'long', day: 'numeric' 
              })}
            </span>
            <span className="flex items-center">
              <Clock size={16} className="mr-2" />
              {Math.ceil(post.content?.length / 1000) || 3} min read
            </span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {post.featured_image && (
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <img 
              src={post.featured_image} 
              alt={post.title}
              className="w-full h-[400px] object-cover rounded-2xl shadow-lg"
            />
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-8 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-border-line">
            <CardContent className="p-8 md:p-12">
              <div className="prose prose-lg max-w-none text-text-secondary">
                {/* Markdown-like rendering */}
                {post.content.split('\n\n').map((paragraph, index) => {
                  if (paragraph.startsWith('# ')) {
                    return <h1 key={index} className="text-3xl font-bold text-text-primary mt-8 mb-4">{paragraph.slice(2)}</h1>;
                  }
                  if (paragraph.startsWith('## ')) {
                    return <h2 key={index} className="text-2xl font-bold text-text-primary mt-6 mb-3">{paragraph.slice(3)}</h2>;
                  }
                  if (paragraph.startsWith('### ')) {
                    return <h3 key={index} className="text-xl font-bold text-text-primary mt-4 mb-2">{paragraph.slice(4)}</h3>;
                  }
                  if (paragraph.startsWith('- ')) {
                    return (
                      <ul key={index} className="list-disc list-inside my-4 space-y-2">
                        {paragraph.split('\n').map((item, i) => (
                          <li key={i}>{item.slice(2)}</li>
                        ))}
                      </ul>
                    );
                  }
                  if (paragraph.startsWith('> ')) {
                    return (
                      <blockquote key={index} className="border-l-4 border-care-teal pl-4 my-6 italic text-text-primary bg-care-teal/5 py-4 rounded-r">
                        {paragraph.slice(2)}
                      </blockquote>
                    );
                  }
                  return <p key={index} className="mb-4 leading-relaxed">{paragraph}</p>;
                })}
              </div>

              {/* Tags */}
              {post.tags?.length > 0 && (
                <div className="mt-8 pt-8 border-t border-border-line">
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag) => (
                      <span key={tag} className="inline-flex items-center px-3 py-1 rounded-full bg-tech-blue/10 text-tech-blue text-sm">
                        <Tag size={12} className="mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Results Highlight */}
          <Card className="mt-8 border-care-teal bg-care-teal/5">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold text-text-primary mb-4 flex items-center">
                <TrendingUp className="mr-2 text-care-teal" size={24} />
                Key Results
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-white rounded-xl">
                  <div className="text-3xl font-bold text-care-teal">60%</div>
                  <div className="text-sm text-text-secondary">Admin Time Saved</div>
                </div>
                <div className="text-center p-4 bg-white rounded-xl">
                  <div className="text-3xl font-bold text-care-teal">99.8%</div>
                  <div className="text-sm text-text-secondary">Compliance Rate</div>
                </div>
                <div className="text-center p-4 bg-white rounded-xl">
                  <div className="text-3xl font-bold text-care-teal">2 Weeks</div>
                  <div className="text-sm text-text-secondary">Deployment Time</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <Card className="mt-8 border-tech-blue bg-tech-blue/5">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold text-text-primary mb-2">
                Ready to achieve similar results?
              </h3>
              <p className="text-text-secondary mb-6">
                Book a free strategy call and see how GetCare can transform your NDIS operations
              </p>
              <Button onClick={openBooking} className="bg-tech-blue hover:bg-tech-blue/90 text-white px-8 py-6 text-lg">
                  Book Free Strategy Call
                </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseStudyListPage;
